import { useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import {
  eldBrandLabel,
  ExistingCompany,
  transferErrorData,
  transferErrorMessage,
  transferErrorStatus,
  TransferMode,
  transferTargets,
} from '../../../../../../API/TT_ELD/companyTransfer';
import { useStartTransfer } from '../../../../../../Hooks/TT_ELD/CompanyTransfer';
import { useProjectData } from '../../../../../../Hooks/useProjectData';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../Utils/eldTypeLabels';
import { TransferBadge } from './transferLabels';

const { Text, Paragraph } = Typography;

interface Props {
  companyId: string;
  company: any;
  onClose(): void;
}

// Closes only from the X icon (not the mask or Esc), so a stray click can't lose the setup
const TransferModal = ({ companyId, company, onClose }: Props) => {
  const { projectData } = useProjectData();
  const currentEld: string | undefined = projectData?.projectName;
  const start = useStartTransfer(companyId);

  const [targetEld, setTargetEld] = useState<string | undefined>();
  const [targetLegal, setTargetLegal] = useState<boolean | undefined>();
  const [includeTracking, setIncludeTracking] = useState(true);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set when the target already has an earlier, inactive record of this company
  const [existing, setExisting] = useState<ExistingCompany | null>(null);
  const [mode, setMode] = useState<TransferMode>('continue');

  const companyName: string = company?.name || `Company #${companyId}`;
  const matches = typed.trim().toLowerCase() === companyName.trim().toLowerCase();
  const canStart = !!targetEld && targetLegal !== undefined && matches;
  const target = eldBrandLabel(targetEld);

  const submit = async (withMode?: TransferMode) => {
    if (!targetEld || targetLegal === undefined) return;
    setError(null);
    try {
      await start.mutateAsync({ targetEld, targetLegal, includeTracking, ...(withMode ? { mode: withMode } : {}) });
      message.success('Company transfer started');
      onClose();
    } catch (err) {
      const existingCompany: ExistingCompany | undefined = transferErrorData(err)?.existingCompany;
      if (transferErrorStatus(err) === 409 && existingCompany && !existingCompany.isActive && !withMode) {
        setExisting(existingCompany);
        return;
      }
      setError(transferErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      title={existing ? 'Earlier record found' : `Transfer ${companyName} to another ELD`}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {error && <Alert type="error" showIcon message={error} />}

        {existing ? (
          <>
            <Paragraph style={{ margin: 0 }}>
              {target} already has an earlier record of this company. Choose what to do with it:
            </Paragraph>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Name">{existing.name}</Descriptions.Item>
              <Descriptions.Item label="USDOT">{existing.usdot}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <TransferBadge eldStatus={existing.eldStatus} /> {existing.isActive ? 'Active' : 'Inactive'}
              </Descriptions.Item>
              {existing.eldStatusDescription && (
                <Descriptions.Item label="Last change">{existing.eldStatusDescription}</Descriptions.Item>
              )}
            </Descriptions>
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
              <Space direction="vertical">
                <Radio value="continue">
                  <Text strong>Continue the earlier record</Text>
                  <div>
                    <Text type="secondary">
                      Reopens it. Drivers (by username) and vehicles (by VIN or truck number) are matched and updated,
                      missing ones are created.
                    </Text>
                  </div>
                </Radio>
                <Radio value="new">
                  <Text strong>Create a new company</Text>
                  <div>
                    <Text type="secondary">The earlier record stays closed.</Text>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Button onClick={() => setExisting(null)}>Back</Button>
              <Button type="primary" danger loading={start.isLoading} onClick={() => submit(mode)}>
                Start transfer
              </Button>
            </Space>
          </>
        ) : (
          <>
            <div>
              <Text>Target ELD</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Choose the ELD to transfer to"
                value={targetEld}
                onChange={setTargetEld}
                options={transferTargets(currentEld).map((eld) => ({ value: eld, label: eldBrandLabel(eld) }))}
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div>
              <Text>Company type on the target</Text>
              <div>
                <Radio.Group value={targetLegal} onChange={(e) => setTargetLegal(e.target.value)}>
                  <Radio value={true}>{STANDARD_ELD}</Radio>
                  <Radio value={false}>{DEFAULT_ELD}</Radio>
                </Radio.Group>
              </div>
            </div>
            <Space>
              <Switch checked={includeTracking} onChange={setIncludeTracking} />
              <Text>Include the last 24 hours of tracking</Text>
            </Space>

            <Alert
              type="warning"
              showIcon
              message="What happens"
              description={
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  <li>
                    The company, vehicles and drivers move to {targetEld ? target : 'the target ELD'}. Drivers keep the same
                    login and password.
                  </li>
                  <li>The last 8 days of logs arrive as requested logs that drivers accept in the app.</li>
                  <li>
                    On this ELD the company is closed and becomes read-only, and all drivers are logged out right away.
                  </li>
                  <li>
                    <Text strong>Not transferred:</Text> dispatchers (add them on the target manually) and Stripe (no
                    customer or subscription is created on the target).
                  </li>
                </ul>
              }
            />

            <div>
              <Paragraph style={{ marginBottom: 4 }}>
                Type the company name <Text strong>{companyName}</Text> to confirm:
              </Paragraph>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={companyName} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <Button type="primary" danger disabled={!canStart} loading={start.isLoading} onClick={() => submit()}>
                Start transfer
              </Button>
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default TransferModal;
